import React from 'react';
import { Formik, Form } from 'formik';
import validationSchema from './validationSchema';
import { FormPrompt } from '@front-activerse/common-components';
import { Modal, ModalHeader, ModalBody, Button, Row, Col } from 'reactstrap';
import createFormikInitialValues from './helpers/createFormikInitialValues';
import { withMobileAppMode, withLoading } from '@front-activerse/common-components';
import UniversalPlaceholder from '../Layout/UniversalPlaceholder';
import { goalSettings } from './types';

import SlideRoutes from 'react-slide-routes';
import StepProvider from './StepContext/StepProvider';
import StepWrapper from './components/StepWrapper';
import { Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import nutritionSchema from './stepsSchema';
import Layout from '../Layout/Layout';
import { useTranslator, useAppContext, IRoute } from '@front-activerse/common-components';

interface IGoalSettingsContainer {
  descendantRoutes: IRoute[];
  goalSettingsData: goalSettings;
  mobileAppMode: boolean;
  saveGoalSettings: (values: goalSettings) => void;
  validateGoalSettings: (values: goalSettings) => void;
}

const GoalSettingsContainer: React.FC<IGoalSettingsContainer> = ({
  descendantRoutes,
  goalSettingsData,
  mobileAppMode,
  saveGoalSettings,
  validateGoalSettings,
}) => {
  const { t } = useTranslator();
  const location = useLocation();
  const {
    consts: { SLIDE_ROUTES_DURATION },
  } = useAppContext();

  // Determine keyword based on context - if we're in all-settings, use "all-settings"
  // otherwise use correct "nutrition-settings" (was incorrectly "goal-settings")
  const formPromptKeyword = location.pathname.includes('/all-settings/')
    ? ['all-settings', 'tracker/settings']
    : ['nutrition-settings', 'tracker/settings'];

  return (
    <Layout scrollY={false}>
      <div className="page-template-goal-settings">
        <Formik
          initialValues={{ ...createFormikInitialValues(goalSettingsData) }}
          validationSchema={() => validationSchema(t)}
          validateOnChange
          onSubmit={(values) => console.log(values)}
        >
          {({ dirty }) => (
            <Form>
              {!mobileAppMode && (
                <FormPrompt hasUnsavedChanges={dirty} keyword={formPromptKeyword}>
                  {({
                    showModal,
                    toggleModal,
                    handleClick,
                  }: {
                    showModal: boolean;
                    toggleModal: () => void;
                    handleClick: () => void;
                  }) => (
                    <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
                      <ModalHeader toggle={toggleModal} />
                      <ModalBody className="pt-0">
                        <h2 className="title-medium semi-bold text-center">
                          {t('warning.unsavedData.title')}
                        </h2>
                        <p className="text-center">{t('warning.unsavedData.content')}</p>
                        <Row>
                          <Col>
                            <Button
                              color="secondary"
                              className="w-100"
                              outline
                              onClick={toggleModal}
                            >
                              {t('warning.unsavedData.cancelButton')}
                            </Button>
                          </Col>
                          <Col>
                            <Button color="primary" className="w-100" onClick={handleClick}>
                              {t('warning.unsavedData.leaveButton')}
                            </Button>
                          </Col>
                        </Row>
                      </ModalBody>
                    </Modal>
                  )}
                </FormPrompt>
              )}
              <StepProvider
                currentMainStep="nutrition"
                schema={nutritionSchema}
                saveGoalSettings={saveGoalSettings}
                validateGoalSettings={validateGoalSettings}
              >
                <div className="full-height2">
                  <Header title={t('nutritionSettings.progressBarTitle')} />
                  <SlideRoutes duration={+SLIDE_ROUTES_DURATION}>
                    {descendantRoutes.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<StepWrapper>{route.element}</StepWrapper>}
                      />
                    ))}
                  </SlideRoutes>
                </div>
                <Footer />
              </StepProvider>
            </Form>
          )}
        </Formik>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder fullHeightMinusHeader />;

export default withMobileAppMode(withLoading(GoalSettingsContainer, Placeholder));
